import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from "react-router";
import { useMutation } from "@tanstack/react-query";

import { NotificationContext, handleError,} from "../context/NotificationContext";

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import { getBasketData, refetchBasketData } from "../queries/global";
import InstanceEventSummary from '../components/instance-event-summary';
import SeatSelectionSummary from '../book-online/seat-selection-summary';
import LoadingIndicator from '../components/loading-indicator';
import DonationBlock from './donation-block';
import ReadMore from '../components/read-more';
import { useMediaQuery } from '../hook/useMediaQuery';
import TransactionCommissionMessage from '../components/transaction-commission-message';

const campaign_name_spektrix = config.ATTRIBUTES?.DonationUpsellCampaignName;
const ticketDonations = config.OPTION_FIELDS.donation_campaign?.find( donation => donation.spektrix_attribute_name === campaign_name_spektrix);
const campaign_name = ticketDonations?.campaign_name;
const main_text = ticketDonations?.main_text;
const donation_precentage =  parseInt(ticketDonations?.suggested_donation_percentage?.innerHTML) || 10;
const fund_id = ticketDonations?.spektrix_donation_fund;
const thank_you_message_message = ticketDonations?.thank_you_message_message;
const thank_you_message_title = ticketDonations?.thank_you_message_title;

const advisory_message = config.OPTION_FIELDS.booking_advisory_message;

export default function TicketDonations(props) {
    let navigate = useNavigate();
    const { instanceId } = useParams();

    const { notification, setNotification } = useContext(NotificationContext);    
    const { data: basketData, isFetching: basketDataFetching } = getBasketData();
    const isMobile = useMediaQuery(`(max-width: 768px)`);
    const dialogRef = useRef(null);

    const [rangeMax, setRangeMax] = useState(null);
    const [selectedSeatCount, setSelectedSeatCount] = useState(0);
    const [seatTicketIsPending, setSeatTicketIsPending] = useState(null);
    const [donationsTotal, setDonationsTotal] = useState(null);
    const [donationId, setDonationId] = useState(null);

    const shouldRedirect = !campaign_name_spektrix || !ticketDonations;

    useEffect(() => {
        if (shouldRedirect) {
            navigate(props.continueLink);
        }
    }, [shouldRedirect]);

    if (shouldRedirect) return null;

    useEffect(() => {
         if (!basketData || !basketData?.tickets) {
           setRangeMax(null);
           return;
         }
     
         // To show in the continue button
         let tickets = UTILS.getTicketsByInstance(basketData.tickets);
         if (!tickets?.length) {
           setRangeMax(null);
           setDonationsTotal(0);
           return;
         }
     
         setSelectedSeatCount(tickets.length);
     
         const tickets_total = UTILS.getTicketsTotal(tickets);

         const donations_total = UTILS.getDonationsTotal(basketData.donations);
         const donation_in_basket = basketData.donations?.find((donation) => donation.fund?.id == fund_id);

         setRangeMax(tickets_total);
         setDonationsTotal(donations_total);

        if (donation_in_basket) {
            setDonationId(donation_in_basket.id);
        }

    }, [basketData]);

    return (

        <div className="booking-pathway__container booking-pathway__ticket-donations">          
        
            <InstanceEventSummary showAlternativeDates="true" />

            { !isMobile && <SeatSelectionSummary  setCheckedForPriority = {props.setCheckedForPriority}/> }
            
            <div className="booking-pathway__main-content">
                <div className='booking-pathway__ticket-donations__header'>
                    { campaign_name.length && 
                        <h1 className='booking-pathway__header'>{campaign_name}</h1>
                    }

                    {main_text && 
                        !isMobile ?
                            <div className="booking-pathway__ticket-donations__content"
                                dangerouslySetInnerHTML={{ __html: main_text }}></div>
                            : <ReadMore text = {main_text} limit={200} />
                    }
                </div>
                <DonationBlock donationPrecentage = {donation_precentage} fundId = {fund_id} rangeMax={rangeMax} donationId = {donationId}/>
            </div>


            {
                !!donationsTotal && (thank_you_message_message.length || thank_you_message_title.length) &&
                <div className='booking-pathway__thanks-message'>
                    <h5 className='booking-pathway__thanks-message__title type-h5'>{thank_you_message_title}</h5>
                    <p>{thank_you_message_message}</p>
                </div>
            }
            
            { !isMobile &&  
                <div className="booking-pathway__advisory-message">
                        {  advisory_message && 
                          <div className="booking-pathway__advisory-message-text"
                              dangerouslySetInnerHTML={{
                                          __html:advisory_message.innerHTML,
                                        }}
                          >          
                          </div>
                        }
                        <TransactionCommissionMessage/>
                      </div>
            }

            <div className="booking-pathway__footer">
                <button
                className={`btn ${!!donationsTotal ? 'btn--primary' : null }`}
                type="button"
                role="link"
                onClick={() => {
                    navigate(props.continueLink);
                }}
                disabled={
                    basketDataFetching ||
                    seatTicketIsPending
                }
                >
                {basketDataFetching || seatTicketIsPending ? (
                    <LoadingIndicator />
                ) : (
                    !!!donationsTotal ? "Continue without donation" : "Continue"
                )}
                </button>
                
                { isMobile &&
                        <>
                        <button type="button" className="btn" onClick={()=>{dialogRef.current.showModal()}}>Show basket summary</button>
                        <dialog ref={dialogRef} className="basket-dialog">
                            <div className="dialog-container">
                            <div className="dialog-content">
                                <SeatSelectionSummary setCheckedForPriority = {props.setCheckedForPriority}/>
                                <div className="basket-dialog__footer">
                                <div className="buttons">
                                <button
                                    className={`btn ${!!donationsTotal ? 'btn--primary' : null }`}
                                    type="button"
                                    role="link"
                                    onClick={() => {
                                        navigate(props.continueLink);
                                    }}
                                    disabled={
                                        basketDataFetching ||
                                        seatTicketIsPending ||
                                        !rangeMax
                                    }
                                    >
                                    {basketDataFetching || seatTicketIsPending ? (
                                        <LoadingIndicator />
                                    ) : (
                                        !!!donationsTotal ? "Continue without donation" : "Continue"
                                    )}
                                    </button>
                                <button type="button" className="btn" onClick={()=>{dialogRef.current?.close()}}>Hide basket summary</button>                                      
                                </div>
                                <div className="booking-pathway__advisory-message">
                                        {  advisory_message && 
                                          <div className="booking-pathway__advisory-message-text"
                                              dangerouslySetInnerHTML={{
                                                          __html:advisory_message.innerHTML,
                                                        }}
                                          >          
                                          </div>
                                        }
                                        <TransactionCommissionMessage/>
                                      </div>
                                </div>
                            </div>
                            </div>
                        </dialog>
                        </>
                        }
                
            </div>            
        </div>
    );
}
